import { useState } from 'react'
import { toast } from 'sonner'

import { useRequoteOnSave } from '@/components/quotes/summary/requoteOnSave'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { BusinessOwner } from '../../models/user'
import { OwnerInfoFields } from './OwnerInfoFields'
import { ownerSaveErrorMessage, useOwnerInfoForm } from './useOwnerInfoForm'

interface EditOwnerInfoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  owner: BusinessOwner | undefined
  orgId: string
}

export function EditOwnerInfoDialog({
  open,
  onOpenChange,
  onSuccess,
  owner,
  orgId,
}: EditOwnerInfoDialogProps) {
  const [loading, setLoading] = useState(false)
  const requote = useRequoteOnSave()
  const editor = useOwnerInfoForm(owner)

  function close() {
    editor.discard()
    onOpenChange(false)
  }

  async function onSubmit() {
    setLoading(true)
    try {
      const result = await editor.save(orgId, { onlyChanged: false })
      if (!result.ok) {
        if (result.error) toast.error(ownerSaveErrorMessage(result.error))
        return
      }
      onSuccess()
      if (requote) await requote.run()
      else toast.success('Owner info updated')
      onOpenChange(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? onOpenChange(true) : close())}>
      <DialogContent>
        <form onSubmit={editor.form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Edit owner info</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <OwnerInfoFields form={editor.form} />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              loading={loading ? (requote?.pendingLabel ?? 'Saving...') : undefined}
            >
              {requote?.label ?? 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
